// src/mocks/handlers/funding/activityAdvanceMovementsHandler.ts

import { rest } from 'msw';
import activityAdvanceMovements from '@/mocks/data/fundingActivityAdvanceMovementsData.json';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import { IAdvanceMovement } from '@/domains/funding/activity.types';
import { API_DEFAULT_PAGE_SIZE } from '@/services/api/api.constants';
import { applyActivityOrdering } from '@/mocks/lib/applyActivityOrdering';
import { matchesDateRange } from '@/mocks/lib/matchesDateRange';
import { matchesAgencyFilter } from '@/mocks/lib/matchesAgencyFilter';
import { PaginatedResponse } from '@/shared/types/api';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';

export const fundingActivityAdvanceMovementsHandler = rest.get<
  PaginatedResponse<IAdvanceMovement>
>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.FUNDING_ACTIVITY_ADVANCE_MOVEMENTS}`,
  (req, res, ctx) => {
    const advanceMovements = activityAdvanceMovements as IAdvanceMovement[];
    const token = req.headers.get('Authorization');
    if (!token) {
      return res(ctx.status(500), ctx.json({ detail: 'Something went wrong' }));
    }

    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    const { customer_uuid } = req.params;

    const url = new URL(req.url);
    const params = url.searchParams;

    const currencies = params.getAll('currencies');
    const networks = params.getAll('networks');
    const sources = params.getAll('sources');
    const agencies = params.getAll('agencies');
    const transaction_types = params.getAll('transaction_types');
    const event_date_start = params.get('event_date_start');
    const event_date_end = params.get('event_date_end');

    const ordering = params.get('ordering') || '';
    const page = parseInt(params.get('page') || '1', 10);
    const page_size = parseInt(
      params.get('page_size') || `${API_DEFAULT_PAGE_SIZE}`,
      10,
    );
    const search = params.get('search') || '';

    // 1. Filter
    let filtered = advanceMovements.filter((item) => {
      const matchCustomer = (item as any).customer_uuid === customer_uuid;
      const matchCurrency =
        currencies.length > 0 ? currencies.includes(item.currency) : true;
      const matchNetwork =
        networks.length > 0
          ? item.network_name != null && networks.includes(item.network_name)
          : true;
      const matchSource =
        sources.length > 0
          ? item.source_name != null && sources.includes(item.source_name)
          : true;
      const matchAgency = matchesAgencyFilter(item.agency_name, agencies);
      const matchTransactionType =
        transaction_types.length > 0
          ? transaction_types.includes(item.transaction_type)
          : true;
      const matchEventDateRange =
        event_date_start && event_date_end
          ? matchesDateRange(item.event_date, event_date_start, event_date_end)
          : true;

      return (
        matchCustomer &&
        matchCurrency &&
        matchNetwork &&
        matchSource &&
        matchAgency &&
        matchTransactionType &&
        matchEventDateRange
      );
    });

    if (search) {
      const lower = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.network_name?.toLowerCase().includes(lower) ||
          item.source_name?.toLowerCase().includes(lower) ||
          item.transaction_id.toLowerCase().includes(lower) ||
          (item.advance_id && item.advance_id.toLowerCase().includes(lower)),
      );
    }

    // 2. Sort (default matches backend when ordering is omitted)
    filtered = applyActivityOrdering(filtered, ordering);

    // 3. Pagination
    const total = filtered.length;
    const pages = Math.ceil(total / page_size);
    const start = Math.max(0, (page - 1) * page_size);
    const end = Math.min(start + page_size, total);
    const results = filtered.slice(start, end);

    // 4. Response
    return res(
      ctx.status(200),
      ctx.json({
        total,
        page,
        pages,
        results,
      }),
    );
  },
);
